import { useCallback, useEffect, useRef, useState } from "react";
import i18n from "@/lib/i18n";

const DEFAULT_LEADER_TIMEOUT_MS = 4_000;
const MAX_PICKER_ITEMS = 9;

export interface LeaderKeySequence {
  code: string;
  label: string;
  action: () => void | Promise<void>;
}

export interface LeaderKeyPickerItem {
  id: string;
  label: string;
}

export interface LeaderKeyPickerLoadResult {
  items: readonly LeaderKeyPickerItem[];
  /**
   * IDs to promote in their persisted order. Items not listed here retain the
   * source order supplied by the loader.
   */
  pinnedItemIds?: readonly string[];
  /**
   * Optional stale-while-revalidate replacement. The picker renders `items`
   * immediately, then swaps to this result if it arrives before dismissal.
   */
  backgroundRefresh?: Promise<LeaderKeyPickerLoadResult | null>;
}

export interface LeaderKeyPicker {
  code: string;
  label: string;
  load: () => Promise<LeaderKeyPickerLoadResult>;
  onSelect: (item: LeaderKeyPickerItem) => void | Promise<void>;
  loadingLabel: string;
  emptyLabel: string;
  unavailableLabel: string;
}

export type LeaderKeyCommand = LeaderKeySequence | LeaderKeyPicker;

export type LeaderKeyPickerStatus = "loading" | "ready" | "empty" | "unavailable";

export interface LeaderKeyPickerState {
  label: string;
  status: LeaderKeyPickerStatus;
  items: readonly LeaderKeyPickerItem[];
  message: string;
}

export interface UseLeaderKeysOptions {
  timeoutMs?: number;
}

function hasOpenRadixDialog(): boolean {
  return document.querySelector("[role='dialog'][data-state='open']") !== null;
}

function isLeaderShortcut(
  event: Pick<KeyboardEvent, "code" | "ctrlKey" | "metaKey" | "repeat">,
): boolean {
  return (
    (event.metaKey || event.ctrlKey) &&
    event.code === "KeyG" &&
    !event.repeat
  );
}

function isPicker(command: LeaderKeyCommand): command is LeaderKeyPicker {
  return "load" in command;
}

function pickerItemIndex(code: string): number | null {
  const match = /^(?:Digit|Numpad)([1-9])$/.exec(code);
  return match ? Number(match[1]) - 1 : null;
}

/**
 * Move pinned entries to the front without changing either the persisted pin
 * order or the source order of the remaining entries.
 */
export function orderLeaderPickerItems(
  items: readonly LeaderKeyPickerItem[],
  pinnedItemIds: readonly string[] = [],
): LeaderKeyPickerItem[] {
  const uniqueItems: LeaderKeyPickerItem[] = [];
  const itemById = new Map<string, LeaderKeyPickerItem>();

  for (const item of items) {
    if (!item.id || itemById.has(item.id)) continue;
    itemById.set(item.id, item);
    uniqueItems.push(item);
  }

  const ordered: LeaderKeyPickerItem[] = [];
  const selectedIds = new Set<string>();
  for (const id of pinnedItemIds) {
    const item = itemById.get(id);
    if (!item || selectedIds.has(id)) continue;
    selectedIds.add(id);
    ordered.push(item);
  }

  return [...ordered, ...uniqueItems.filter((item) => !selectedIds.has(item.id))];
}

/**
 * Global serial Cmd/Ctrl+G leader-key shortcut state.
 *
 * Pressing the leader arms the next keydown for a short, configurable window.
 * Registered sequences use physical keyboard codes so their behavior is
 * independent of the active keyboard layout.
 */
export function useLeaderKeys(
  sequences: readonly LeaderKeyCommand[],
  { timeoutMs = DEFAULT_LEADER_TIMEOUT_MS }: UseLeaderKeysOptions = {},
) {
  const [isPending, setIsPending] = useState(false);
  const [pendingPicker, setPendingPicker] = useState<LeaderKeyPickerState | null>(null);
  const isPendingRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sequencesRef = useRef(sequences);
  const activePickerRef = useRef<{
    requestId: number;
    picker: LeaderKeyPicker;
    items: readonly LeaderKeyPickerItem[];
  } | null>(null);
  const pickerRequestIdRef = useRef(0);
  sequencesRef.current = sequences;

  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current === null) return;
    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const disarm = useCallback(() => {
    clearPendingTimeout();
    pickerRequestIdRef.current += 1;
    activePickerRef.current = null;
    isPendingRef.current = false;
    setIsPending(false);
    setPendingPicker(null);
  }, [clearPendingTimeout]);

  const arm = useCallback(() => {
    clearPendingTimeout();
    pickerRequestIdRef.current += 1;
    activePickerRef.current = null;
    isPendingRef.current = true;
    setIsPending(true);
    setPendingPicker(null);
    timeoutRef.current = setTimeout(disarm, timeoutMs);
  }, [clearPendingTimeout, disarm, timeoutMs]);

  const enterPicker = useCallback((picker: LeaderKeyPicker) => {
    clearPendingTimeout();
    const requestId = pickerRequestIdRef.current + 1;
    pickerRequestIdRef.current = requestId;
    activePickerRef.current = { requestId, picker, items: [] };
    isPendingRef.current = true;
    setIsPending(true);
    setPendingPicker({
      label: picker.label,
      status: "loading",
      items: [],
      message: picker.loadingLabel,
    });
    timeoutRef.current = setTimeout(disarm, timeoutMs);

    const applyPickerResult = (
      result: LeaderKeyPickerLoadResult,
      requestId: number,
    ): boolean => {
      if (!result || !Array.isArray(result.items)) return false;

      const items = orderLeaderPickerItems(
        result.items,
        Array.isArray(result.pinnedItemIds) ? result.pinnedItemIds : [],
      ).slice(0, MAX_PICKER_ITEMS);
      const activePicker = activePickerRef.current;
      if (!activePicker || activePicker.requestId !== requestId) return false;

      activePickerRef.current = { ...activePicker, items };
      setPendingPicker({
        label: picker.label,
        status: items.length > 0 ? "ready" : "empty",
        items,
        message: items.length > 0 ? "" : picker.emptyLabel,
      });
      return true;
    };

    void (async () => {
      try {
        const result = await picker.load();
        if (!applyPickerResult(result, requestId)) {
          if (!result || !Array.isArray(result.items)) {
            throw new Error(i18n.t("leaderKeys.errors.invalidResponse"));
          }
          // The picker may have been dismissed while its initial snapshot was
          // resolving. The refresh is already in flight, so consume a later
          // rejection rather than leaving an unhandled promise behind.
          void result.backgroundRefresh?.catch(() => {});
          return;
        }

        if (!result.backgroundRefresh) return;
        void result.backgroundRefresh
          .then((refreshed) => {
            if (refreshed) applyPickerResult(refreshed, requestId);
          })
          .catch(() => {
            // A stale snapshot is still usable; background failures should
            // never replace it with an unavailable picker.
          });
      } catch {
        const activePicker = activePickerRef.current;
        if (!activePicker || activePicker.requestId !== requestId) return;

        activePickerRef.current = { ...activePicker, items: [] };
        setPendingPicker({
          label: picker.label,
          status: "unavailable",
          items: [],
          message: picker.unavailableLabel,
        });
      }
    })();
  }, [clearPendingTimeout, disarm, timeoutMs]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // Composer-owned shortcuts and active dialogs get first refusal.
      if (event.defaultPrevented) return;

      if (isPendingRef.current) {
        if (event.repeat) return;

        // The follow-up belongs to the leader sequence, even when it does not
        // match. This prevents it from being inserted into a focused editor.
        event.preventDefault();

        if (event.code === "Escape") {
          disarm();
          return;
        }

        const activePicker = activePickerRef.current;
        if (activePicker) {
          const itemIndex = pickerItemIndex(event.code);
          const item = itemIndex === null ? undefined : activePicker.items[itemIndex];
          disarm();
          if (item) void activePicker.picker.onSelect(item);
          return;
        }

        if (isLeaderShortcut(event)) {
          arm();
          return;
        }

        const sequence = sequencesRef.current.find(
          (candidate) => candidate.code === event.code,
        );
        if (sequence && isPicker(sequence)) {
          enterPicker(sequence);
          return;
        }

        disarm();
        if (sequence) void sequence.action();
        return;
      }

      if (!isLeaderShortcut(event)) return;
      if (hasOpenRadixDialog()) return;

      event.preventDefault();
      arm();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [arm, disarm, enterPicker]);

  useEffect(() => () => {
    clearPendingTimeout();
    isPendingRef.current = false;
    activePickerRef.current = null;
    pickerRequestIdRef.current += 1;
  }, [clearPendingTimeout]);

  const pendingSequences: readonly LeaderKeyCommand[] = isPending && !pendingPicker ? sequences : [];

  return { isPending, pendingSequences, pendingPicker, disarm };
}
